import React, { useState, useEffect } from "react";
import Cookies from "js-cookie";
import httpClient from "../../../../../../api/httpClient";
import notify from "../../../../../../components/ACS-Dashboard/ACS-Blocks/Toast";
import { useNavigate } from "react-router-dom";

const StopReasonModal = ({
  onClose,
  shipmentDetails,
  shipmentNumber,
  fetchShipmentDetails,
  isShipper,
  isPickup,
  isDropoff,
  onStopReasonAdded,
}) => {
  const [stopReason, setStopReason] = useState("");
  const [stopReasonCodes, setStopReasonCodes] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [stopReasonAdded, setStopReasonAdded] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const navigate = useNavigate();

  // LGX-666: "Add Stop Reason" let a user re-pick the reason an unlimited number of times with
  // no warning. If the pickup stop already has one set, require an explicit confirmation before
  // exposing the dropdown at all.
  const existingPickupStop = shipmentDetails?.shipmentStops?.find(
    (stop) => stop.stopType === "PICKUP"
  );
  const existingStopReasonName =
    existingPickupStop?.stopReasonName || existingPickupStop?.stopReasonCode || "";
  const [changeConfirmed, setChangeConfirmed] = useState(false);
  const needsChangeConfirmation = Boolean(existingStopReasonName) && !changeConfirmed;

  const handleSaveStopReasonAndCreateAppointment = async () => {
    if (!stopReason) {
      notify.error({ title: "Missing stop reason", message: "Please select a stop reason first." });
      return;
    }

    // Find the PICKUP stop specifically
    const pickupStop = shipmentDetails?.shipmentStops?.find(
      (stop) => stop.stopType === "PICKUP"
    );

    if (!pickupStop) {
      notify.error({ title: "Not found", message: "Pickup stop not found." });
      return;
    }

    // Find the selected stop reason code
    const selectedCode = stopReasonCodes.find(code => code.description === stopReason);
    const stopReasonCode = selectedCode ? selectedCode.code : "";

    const payload = {
      shipmentStops: [
        {
          id: pickupStop.id,
          stopNumber: pickupStop.stopNumber,
          stopReasonCode: stopReasonCode,
        },
      ],
    
    };

    const appointmentData = {
      shipmentNumber: shipmentDetails.shipmentNumber,
      company: shipmentDetails.shipperName,
      addressLine1: pickupStop.addressLine1,
      addressLine2: pickupStop.addressLine2,
      city: pickupStop.city,
      state: pickupStop.state,
      zipCode: pickupStop.zipCode,
      country: pickupStop.country,
      stopReasonCode: stopReasonCode,
      carrierName: shipmentDetails.carrierOfficeCode?.name || "",
      carrierOfficeCode: shipmentDetails.carrierOfficeCode?.partyOfficeCode || "",
      shipperName: shipmentDetails.shipperOfficeCode?.name || "",
      shipperOfficeCode: shipmentDetails.shipperOfficeCode?.partyOfficeCode || "",
      location: shipmentDetails.shipperOfficeCode?.locationRef || "",
      poNumber: shipmentDetails.poNumber || "",
      saleNumber: shipmentDetails.saleNumber || "",
    };

    let response;
    try {
      setIsSubmitting(true);
      const freshToken = Cookies.get("authToken");
      // const freshOfficeCode = Cookies.get("officeCode");
      const freshOfficeCode = shipmentDetails.officeCode;

      response = await httpClient.put(
        `/api/shipments/${shipmentNumber}`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${freshToken}`,
            "Office-Code": freshOfficeCode,
          },
        }
      );
    } catch (error) {
      console.error("Stop reason update error:", error);
      console.error("Full Axios Error:", error.response?.data || error.message);
      notify.error({ title: "Failed to add stop reason", message: error?.response?.data?.message || error?.message || "Unexpected error occurred.",
            });
      return;
    } finally {
      setIsSubmitting(false);
    }

    if (response && response.status === 200) {
      const prettyReason = stopReason === "Load" ? "Live Load" : stopReason;
  notify.success({     title: "Stop Reason Added",      message: `Reason: ${prettyReason}`,    });
      fetchShipmentDetails();

      onStopReasonAdded(stopReasonCode);
    }
    onClose();
    
    // if (stopReasonCode === "LD" && appointmentData) {
    //   navigate("/dashboard/appointments?createAppointment=true", {
    //     state: { appointmentData },
    //   });
    // } else if (stopReasonCode === "DT" && appointmentData) {
    //   toast.error("Only shipper can create appointment");
    //   onClose();
    // }



  };

  // Fetch stop reason codes
  useEffect(() => {
    const fetchStopReasonCodes = async () => {
      try {
        const response = await httpClient.get(
          `/api/code-mapping/type/stop_reason`,
          {
            headers: {
              Authorization: `Bearer ${Cookies.get("authToken")}`,
              "Office-Code": shipmentDetails?.officeCode,
            },
          }
        );
        const normalize = (s) =>
          (s ?? "")
            .toString()
            .trim()
            .toUpperCase()
            .replace(/\s+/g, " ");
        const options = Array.isArray(response.data) ? response.data : [];
        const filtered = options.filter((opt) => {
          const code = normalize(opt.code);
          const desc = normalize(opt.description);
          const isDrop =
            code === "DT" ||
            desc === "DROP TRAILER" ||
            (desc.includes("DROP") && desc.includes("TRAILER"));
          const isLive =
            code === "LL" ||
            code === "LD" ||
            desc === "LIVE LOAD" ||
            desc === "LOAD";
          return isDrop || isLive;
        });
        const fallback = [
          { code: "LD", description: "Live Load" },
          { code: "DT", description: "Drop Trailer" },
        ];
        setStopReasonCodes(filtered.length ? filtered : fallback);
              } catch (error) {
        console.error("Error fetching stop reason codes:", error);
        setStopReasonCodes([
          { code: "LD", description: "Live Load" },
          { code: "DT", description: "Drop Trailer" },
        ]);
      }
    };

    fetchStopReasonCodes();
  }, [shipmentDetails?.officeCode]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-custom-bg-purple bg-opacity-50 px-4">
      <div className="bg-white rounded-xl shadow w-full max-w-md p-fluid-3 space-y-fluid-3 animate-slide-in">
        <div className="flex justify-between items-center border-b pb-fluid-2">
          <h3 className="text-fluid-lg font-semibold text-gray-900">
            Add Stop Reason for Pickup Stop
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 text-fluid-xl"
          >
            ✕
          </button>
        </div>

        {needsChangeConfirmation ? (
          <>
            <div className="space-y-fluid-2">
              <p className="text-fluid-sm text-gray-700">
                A stop reason (<span className="font-semibold">{existingStopReasonName}</span>) is
                already set for this shipment&apos;s pickup stop. Are you sure you want to change it?
              </p>
            </div>

            <div className="flex justify-end gap-fluid-2 pt-fluid-2 border-t">
              <button
                onClick={onClose}
                className="px-fluid-button-x py-fluid-button-y border border-gray-300 rounded text-gray-700 hover:bg-gray-100 text-fluid-sm"
              >
                Cancel
              </button>
              <button
                onClick={() => setChangeConfirmed(true)}
                className="px-fluid-button-x py-fluid-button-y text-fluid-sm rounded font-semibold transition bg-amber-500 hover:bg-amber-600 text-white"
              >
                Continue
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="space-y-fluid-2">

                <div>
                  <label className="block text-fluid-sm font-medium text-gray-700">
                    Stop Reason
                  </label>
                  <div className="relative">
                    <button
                      onClick={() => setShowDropdown(!showDropdown)}
                      className="w-full border border-gray-300 rounded px-fluid-input-x py-fluid-input-y text-fluid-base flex justify-between items-center focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <span>
                        {stopReason ? (stopReason === "Load" ? "Live Load" : stopReason) : "Select a stop reason"}
                      </span>
                      <svg
                        className={`ml-2 w-4 h-4 transform transition-transform duration-200 ${showDropdown ? "rotate-180" : ""
                          }`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {showDropdown && (
                      <div className="absolute left-0 right-0 mt-fluid-1 max-h-60 overflow-y-auto bg-white border border-gray-300 rounded shadow-lg z-10 animate-in fade-in-0 zoom-in-95">
                        {stopReasonCodes.map((code) => (
                          <button
                            key={code.id}
                            onClick={() => {
                              setStopReason(code.description);
                              setShowDropdown(false);
                            }}
                            className="w-full text-left px-fluid-2 py-fluid-1.5 hover:bg-gray-100 text-fluid-sm"
                          >
                            {code.description === "Load" ? "Live Load" : code.description}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>

                </div>

            </div>

            <div className="flex justify-end gap-fluid-2 pt-fluid-2 border-t">
              <button
                 onClick={onClose}
                className="px-fluid-button-x py-fluid-button-y border border-gray-300 rounded text-gray-700 hover:bg-gray-100 text-fluid-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveStopReasonAndCreateAppointment}
                disabled={!stopReason}
                className={`px-fluid-button-x py-fluid-button-y text-fluid-sm rounded font-semibold transition ${!stopReason
                    ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                    : "bg-green-600 hover:bg-green-700 text-white"
                  }`}
              >
               Confirm
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default StopReasonModal; 
